import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useUIStore } from '../../store';
import { useConversations } from '../../hooks/useConversations';
import { useChatUnread } from '../../hooks/useChatUnread';
import { resolveLocale } from '../../lib/dateFormat';
import type { GroupConversation, TaskConversation } from '../../types/conversations';
import type { User } from '../../types';
import GroupChatThread from './GroupChatThread';

interface ConversationsPanelProps {
  onClose: () => void;
}

// Global chat dropdown rendered inside the Header's chat button
// (same relative/absolute w-80 pattern as the Bell dropdown).
// NOT a chat screen: a task row navigates to /tasks/:id where
// TaskDetails.tsx already shows its own message panel; a group row
// opens the real GroupChatThread below.
const ConversationsPanel: React.FC<ConversationsPanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { theme, language } = useUIStore();
  const navigate = useNavigate();
  const { taskConversations, groupConversations, loading, loaded, error, refetch } = useConversations();
  const { isUnread, markRead } = useChatUnread(taskConversations, groupConversations, loaded);
  const [selectedGroup, setSelectedGroup] = useState<GroupConversation | null>(null);
  const [members, setMembers] = useState<User[]>([]);

  useEffect(() => {
    api
      .get('/workspace/members')
      .then((res) => {
        const data = (res.data as any)?.data;
        if (Array.isArray(data)) setMembers(data);
      })
      .catch(() => {
        // Non-critical — previews just fall back to the raw author id.
      });
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const initialsOf = (title: string) => {
    const words = title.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return (words[0]?.slice(0, 2) || '?').toUpperCase();
  };

  const previewOf = (authorId: string, message: string | null) => {
    if (!message) return t('globalChat.noMessages');
    const authorName = members.find((m) => m.id === authorId)?.name || authorId;
    return `${authorName}: ${message}`;
  };

  // Leaving an open group thread counts as having read whatever arrived in it.
  const openGroupId = selectedGroup?.id;
  useEffect(() => {
    if (!openGroupId) return;
    return () => markRead('group', openGroupId);
  }, [openGroupId, markRead]);

  const handleTaskClick = (item: TaskConversation) => {
    markRead('task', item.id, item.latest_message.created_at);
    onClose();
    navigate(`/tasks/${item.id}`);
  };

  const rowCls = (isLast: boolean) =>
    `w-full text-left flex items-center gap-3 px-4 py-3 border-b transition-colors duration-200 ${
      theme === 'dark' ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'
    } ${isLast ? 'border-b-0' : ''}`;

  const renderGroupRow = (item: GroupConversation, isLast: boolean) => (
    <button key={item.id} onClick={() => { markRead('group', item.id, item.latest_message.created_at); setSelectedGroup(item); }} className={rowCls(isLast)}>
      <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
        style={{ backgroundColor: item.color || '#3b5bdb' }}>
        {initialsOf(item.name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {item.name}
        </p>
        <p className={`text-xs mt-0.5 truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {previewOf(item.latest_message.author_id, item.latest_message.message)}
        </p>
      </div>
      <span className={`text-xs flex-shrink-0 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
        {formatDate(item.latest_message.created_at)}
      </span>
      {isUnread('group', item) && (
        <span aria-label={t('globalChat.unread')} className="h-2.5 w-2.5 rounded-full bg-red-500 flex-shrink-0" />
      )}
    </button>
  );

  const renderTaskRow = (item: TaskConversation, isLast: boolean) => (
    <button key={item.id} onClick={() => handleTaskClick(item)} className={rowCls(isLast)}>
      <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold ${
        theme === 'dark' ? 'bg-gray-600 text-white' : 'bg-[#e8f7fa] text-[#0e7490]'}`}>
        {initialsOf(item.title)}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {item.title}
        </p>
        <p className={`text-xs mt-0.5 truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {previewOf(item.latest_message.author_id, item.latest_message.message)}
        </p>
      </div>
      <span className={`text-xs flex-shrink-0 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
        {formatDate(item.latest_message.created_at)}
      </span>
      {isUnread('task', item) && (
        <span aria-label={t('globalChat.unread')} className="h-2.5 w-2.5 rounded-full bg-red-500 flex-shrink-0" />
      )}
    </button>
  );

  return (
    <div className={`absolute right-0 mt-2 w-80 rounded-lg shadow-lg border z-50 ${
      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className={`flex items-center justify-between px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {selectedGroup ? selectedGroup.name : t('globalChat.title')}
        </p>
        {selectedGroup ? (
          <button onClick={() => setSelectedGroup(null)}
            className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
            {t('globalChat.back')}
          </button>
        ) : (
          <button onClick={() => refetch()}
            className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
            {t('globalChat.refresh')}
          </button>
        )}
      </div>
      {selectedGroup ? (
        <div className="h-80 flex flex-col">
          <GroupChatThread key={selectedGroup.id} group={selectedGroup} />
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {loading && taskConversations.length === 0 && groupConversations.length === 0 ? (
            <p className={`px-4 py-6 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('common.loading')}
            </p>
          ) : error ? (
            <p className="px-4 py-6 text-center text-sm text-red-500">{error}</p>
          ) : (
            <>
              <p className={`px-4 pt-3 pb-1 text-xs font-bold uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('globalChat.groupsSection')}
              </p>
              {groupConversations.length === 0 ? (
                <p className={`px-4 py-3 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('globalChat.noMessages')}
                </p>
              ) : (
                groupConversations.map((g, i) => renderGroupRow(g, i === groupConversations.length - 1 && taskConversations.length === 0))
              )}
              <p className={`px-4 pt-3 pb-1 text-xs font-bold uppercase tracking-wide border-t ${theme === 'dark' ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-100'}`}>
                {t('globalChat.taskConversationsSection')}
              </p>
              {taskConversations.length === 0 ? (
                <p className={`px-4 py-3 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('globalChat.noMessages')}
                </p>
              ) : (
                taskConversations.map((item, i) => renderTaskRow(item, i === taskConversations.length - 1))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationsPanel;
